/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { useCallback, useEffect, useState } from "react";
import { BLOCKS } from "@contentful/rich-text-types";
import { navigate } from "gatsby";
import { useLocation } from "@reach/router";
import { Options } from "@contentful/rich-text-react-renderer";
import parse from "html-react-parser";

import SectionContainer from "../SectionContainer";
import Pagination from "../Pagination";
import RichText from "../RichTextV2";
import { fixPropWarnings } from "../../utils/fixPropWarnings";
import { FAQSearchParamKey } from "../../types";

import FAQAccordion from "./FAQAccordion";
import { Heading } from "./common";

const StyledSectionContainer = styled(SectionContainer)`
  padding-bottom: 40px;
  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    padding-bottom: 80px;
    padding-top: 18px;
  }
  padding-top: 8px;
`;

const StyledPagination = styled(Pagination)`
  margin-top: 60px;
  align-self: center;
  color: black;
`;

const CategoryTileWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: 16px 12px;
  align-self: stretch;
  flex-wrap: wrap;
  margin-bottom: 21px;

  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    gap: 24px;
  }
`;
const ProductFilterWrapper = styled.div`
  display: flex;
  align-items: baseline;
  align-content: center;
  gap: 24px;
  align-self: stretch;
  flex-wrap: wrap;
  margin-bottom: 24px;
  font-size: 14px;
  justify-content: center;
`;

const ProductFilter = styled.div.withConfig(fixPropWarnings("selected"))<{ selected: boolean }>`
  display: flex;
  color: ${(props) => props.selected ? themeGet("colors.red") : themeGet("colors.black")};
  justify-content: center;
  align-items: center;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  user-select: none;
  text-decoration: underline;
  cursor: pointer;
  text-underline-offset: 4px;
`;

const CategoryTile = styled.div.withConfig(fixPropWarnings("selected"))<{ selected: boolean }>`
  display: flex;
  border-radius: 20px;
  border-style: solid;
  border-width: ${(props) => (props.selected ? "2px" : "1px")};
  border-color: ${(props) => props.selected ? themeGet("colors.red") : themeGet("colors.black")};
  color: ${(props) => props.selected ? themeGet("colors.red") : themeGet("colors.black")};
  padding: ${(props) => (props.selected ? `7px 15px` : "8px 16px")};
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

export const Desktop = styled.div`
  display: none;

  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    display: block;
    width: 100%;
  }
`;

export const Mobile = styled.div`
  background-color: #ffffff;
  display: block;
  margin-bottom: 20px;
  width: 100%;

  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    display: none;
  }
`;

export const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: "";
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid black;
    height: 0;
    width: 0;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export const ProductFilterDropdown = styled.select`
  border: 1px solid #000;
  border-radius: 5px;
  font-weight: bold;
  padding: 10px;
  width: 100%;
  appearance: none;
`;

export const ProductFilterLabel = styled.div`
  background-color: ${themeGet("colors.greys.0")};
  padding: 10px;
  text-align: left;
`;

const HeaderOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
  },
};

const ClearAllOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => children,
  },
};

const SECTION_ID = "faq-accordion";
const ALL_FAQ_RESULTS = "faq-all";
const CLEAR_ALL = "faq-all";

interface PartialFilterSection {
  id: string;
  cmsName: string;
  displayLabel: string;
}

interface FAQPaginatedAccordionV2Props {
  faqItems: any[];
  itemsPerPage?: number;
  categoryFilterSection: any;
  productFiltersSection: any;
}

const FAQPaginatedAccordionV2 = ({
  faqItems,
  itemsPerPage = 50,
  categoryFilterSection,
  productFiltersSection,
}: FAQPaginatedAccordionV2Props) => {
  // State variables
  const { search } = useLocation();
  const [faqAnchor, setFaqAnchor] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const [faqStateItems, setFaqStateItems] = useState<any[]>(faqItems);
  const [currentStep, setCurrentStep] = useState(1);

  const pages = faqStateItems.length / itemsPerPage;
  const pagedItems = faqStateItems.slice(
    (currentStep - 1) * itemsPerPage,
    (currentStep - 1) * itemsPerPage + itemsPerPage,
  );

  // Filtering logic
  const filterPagedItems = useCallback(
    (categoryFilter: string | null, productFilter: string | null) => {
      const productFilteredFaqItems = !productFilter || productFilter === CLEAR_ALL
        ? faqItems
        : faqItems.filter(
          ({ faqParentPage }) => productFilter === `faq-${faqParentPage}`,
        );

      if (categoryFilter === ALL_FAQ_RESULTS || categoryFilter === CLEAR_ALL) {
        setFaqStateItems(productFilteredFaqItems);
      } else {
        setFaqStateItems(
          productFilteredFaqItems.filter(
            (item) => item.filter.cmsName === categoryFilter,
          ),
        );
      }
    },
    [faqItems],
  );

  // Scroll to anchored question
  useEffect(() => {
    const foundIndex = faqStateItems.findIndex(
      (question) => question.anchorName.trim() === faqAnchor,
    );
    const pageNum = Math.floor(foundIndex / itemsPerPage + 1);
    if (pageNum !== 0) {
      setCurrentStep(pageNum);
    }
  }, [faqAnchor, faqStateItems, itemsPerPage]);

  useEffect(() => {
    if (typeof document === "undefined" || !faqAnchor) return;
    document.getElementById(faqAnchor)?.scrollIntoView({ behavior: "smooth" });
  }, [currentStep, faqAnchor]);

  // When url changes, update state variables and re filter
  useEffect(() => {
    const locationSearchParams = new URLSearchParams(search);
    const categoryFilter = locationSearchParams?.get(FAQSearchParamKey.Filter);
    const questionAnchor = locationSearchParams?.get(FAQSearchParamKey.Question);
    const productFilter = locationSearchParams?.get(FAQSearchParamKey.Product);
    const setCategoryFilter = categoryFilter
      ?? categoryFilterSection.combinatorSectionItems?.[0]?.cmsName
      ?? null;
    setCurrentStep(1);
    setFaqAnchor(questionAnchor);
    setCatFilter(setCategoryFilter);
    setSelectedProduct(productFilter ?? CLEAR_ALL);
    filterPagedItems(setCategoryFilter, productFilter);
  }, [categoryFilterSection.combinatorSectionItems, filterPagedItems, search]);

  // When user selects a product, update the url
  const onProductFilterSelect = (productFilter: string) => {
    const locationSearchParams = new URLSearchParams(search);
    const categoryFilter = locationSearchParams?.get(FAQSearchParamKey.Filter);
    const queryParams = [];
    queryParams.push(
      categoryFilter
        ? `${FAQSearchParamKey.Filter}=${encodeURIComponent(categoryFilter)}`
        : `${FAQSearchParamKey.Filter}=${
          catFilter
            || categoryFilterSection.combinatorSectionItems?.[0]?.cmsName
        }`,
    );
    const productParam = `${FAQSearchParamKey.Product}=${encodeURIComponent(productFilter)}`;
    queryParams.push(productParam);
    navigate(`?${queryParams.join("&")}`, { replace: true });
  };

  // When user selects a category filter, update the url
  const onCategoryTileSelect = (selectedCatFilter: string) => {
    if (catFilter === selectedCatFilter) {
      return;
    }
    setFaqAnchor(SECTION_ID);
    const locationSearchParams = new URLSearchParams(search);
    const productFilterQueryParam = locationSearchParams?.get(FAQSearchParamKey.Product);
    const queryParams = [];
    const productParam = `${FAQSearchParamKey.Product}=${encodeURIComponent(
      productFilterQueryParam ?? selectedProduct ?? CLEAR_ALL,
    )}`;

    queryParams.push(productParam);
    queryParams.push(
      catFilter === selectedCatFilter
        ? `${FAQSearchParamKey.Filter}=${ALL_FAQ_RESULTS}`
        : `${FAQSearchParamKey.Filter}=${encodeURIComponent(selectedCatFilter)}`,
    );
    navigate(`?${queryParams.join("&")}`, { replace: true });
  };

  // When user selects a question, update the url
  const onQuestionSelect = (args: string[]) => {
    const locationSearchParams = new URLSearchParams(search);
    if (!args.length) locationSearchParams.delete(FAQSearchParamKey.Question);
    else locationSearchParams.set(FAQSearchParamKey.Question, args[0]);
    const queryParams = locationSearchParams.toString();
    navigate(`?${queryParams}`, { replace: true });
  };

  // to fix hydration issue for styled-components
  const [render, setRender] = useState(false);
  useEffect(() => {
    setRender(true);
  }, []);
  if (!render) return null;

  return (
    <StyledSectionContainer centered>
      {categoryFilterSection && (
        <>
          <RichText
            document={categoryFilterSection.sectionTitle}
            options={HeaderOptions}
          />
          <CategoryTileWrapper id={SECTION_ID}>
            {categoryFilterSection.combinatorSectionItems.map((filter: PartialFilterSection) => (
              <CategoryTile
                key={filter.id}
                selected={catFilter === filter.cmsName}
                onClick={() => onCategoryTileSelect(filter.cmsName)}
              >
                {parse(filter.displayLabel)}
              </CategoryTile>
            ))}
          </CategoryTileWrapper>
        </>
      )}
      {productFiltersSection && (
        <>
          <Desktop>
            <ProductFilterWrapper>
              <div>
                <RichText
                  document={productFiltersSection.sectionTitle}
                  options={ClearAllOptions}
                />
              </div>
              {productFiltersSection.combinatorSectionItems.map(
                (productFilter: PartialFilterSection) => (
                  <ProductFilter
                    key={productFilter.id}
                    selected={selectedProduct === productFilter.cmsName}
                    onClick={() => onProductFilterSelect(productFilter.cmsName)}
                  >
                    {productFilter.displayLabel}
                  </ProductFilter>
                ),
              )}
            </ProductFilterWrapper>
          </Desktop>
          <Mobile>
            <ProductFilterLabel>
              <RichText
                document={productFiltersSection.sectionTitle}
                options={ClearAllOptions}
              />
            </ProductFilterLabel>
            <DropdownWrapper>
              <ProductFilterDropdown
                onChange={(e) => onProductFilterSelect(e.target.value)}
              >
                {productFiltersSection.combinatorSectionItems.map(
                  (productFilter: PartialFilterSection) => (
                    <option
                      key={productFilter.id}
                      value={productFilter.cmsName}
                      selected={
                        selectedProduct === productFilter.cmsName
                        || productFilter.cmsName === CLEAR_ALL
                      }
                    >
                      {productFilter.displayLabel}
                    </option>
                  ),
                )}
              </ProductFilterDropdown>
            </DropdownWrapper>
          </Mobile>
        </>
      )}
      <FAQAccordion
        combinatorSectionItems={pagedItems}
        faqAnchor={faqAnchor ?? undefined}
        onChange={onQuestionSelect}
      />
      {pages > 1 && (
        <StyledPagination
          color={themeGet("colors.velvet")}
          totalArticle={faqStateItems.length}
          articlesPerPage={itemsPerPage}
          currentPage={currentStep}
          paginate={setCurrentStep}
          anchor={faqAnchor ?? undefined}
          noScroll
        />
      )}
    </StyledSectionContainer>
  );
};

export default FAQPaginatedAccordionV2;
